import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Radio, Users, Sparkles, MessageSquare, Zap, Shield, ArrowRight, Play } from "lucide-react";
import { Button } from "@/components/ui/button";
import NexusLogo from "@/components/NexusLogo";
import { useAuth } from "@/context/AuthContext";

const features = [
  { icon: Users, title: "Comunidades vivas", text: "Servidores com canais, cargos, membros e chat em tempo real.", accent: "hsl(217,96%,60%)" },
  { icon: Radio, title: "Lives sem fricção", text: "Descubra criadores e assista com o chat lado a lado.", accent: "hsl(190,92%,55%)" },
  { icon: MessageSquare, title: "Conversa que flui", text: "Menções, respostas, reações e presença ao vivo.", accent: "hsl(300,92%,62%)" },
  { icon: Sparkles, title: "Identidade sua", text: "Perfis de criador com banner, agenda e conquistas.", accent: "hsl(40,96%,56%)" },
  { icon: Zap, title: "Baixa latência", text: "WebSockets nativos para mensagens instantâneas.", accent: "hsl(145,62%,55%)" },
  { icon: Shield, title: "Segurança primeiro", text: "Sessões opacas, cookies HttpOnly, sem token no navegador.", accent: "hsl(217,96%,60%)" },
];

const trending = [
  { title: "Cyber Arena · Grand Final", cat: "FPS", viewers: "12.4K", live: true, from:"from-[hsl(217,60%,20%)]", to:"to-[hsl(224,50%,14%)]" },
  { title: "Estúdio Aurora", cat: "Música", viewers: "3.1K", live: true, from:"from-[hsl(300,50%,22%)]", to:"to-[hsl(224,50%,14%)]" },
  { title: "Devs em Ação", cat: "Tech", viewers: "1.8K", live: true, from:"from-[hsl(190,60%,20%)]", to:"to-[hsl(217,50%,14%)]" },
];

export default function Apresentacao() {
  const { user } = useAuth();
  const goTo = user ? "/app" : "/register";
  return (
    <div className="min-h-screen bg-background text-foreground relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 nx-grid-bg opacity-40 pointer-events-none" />
      <div className="absolute -top-40 -left-40 w-[600px] h-[600px] rounded-full bg-[hsl(var(--nx-blue)/0.18)] blur-[140px] pointer-events-none" />
      <div className="absolute -bottom-40 -right-40 w-[600px] h-[600px] rounded-full bg-[hsl(var(--nx-cyan)/0.14)] blur-[140px] pointer-events-none" />

      {/* Header */}
      <header className="relative z-10 flex items-center justify-between px-6 lg:px-14 py-5">
        <NexusLogo size={34} />
        <nav className="hidden md:flex items-center gap-1 nx-glass rounded-full px-2 py-1.5">
          {[
            { l: "Início", to: "/" },
            { l: "Explorar", to: "/app/explorar" },
            { l: "Comunidades", to: "/app" },
            { l: "Categorias", to: "/app/explorar" },
          ].map((n) => (
            <Link key={n.l} to={n.to} className="px-3.5 py-1.5 text-sm text-muted-foreground hover:text-foreground rounded-full hover:bg-white/5 transition-colors">
              {n.l}
            </Link>
          ))}
        </nav>
        <div className="flex items-center gap-2">
          <Link to="/login">
            <Button variant="ghost" data-testid="landing-login-btn" className="rounded-full hover:bg-white/5">Entrar</Button>
          </Link>
          <Link to="/register">
            <Button data-testid="landing-register-btn" className="rounded-full nx-btn-blue border-0 px-5 font-semibold">
              Criar conta
            </Button>
          </Link>
        </div>
      </header>

      {/* Hero */}
      <main className="relative z-10 px-6 lg:px-14 pt-8 lg:pt-16 pb-24 max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-[minmax(0,1fr)_minmax(0,520px)] gap-14 items-center">
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <span className="nx-chip nx-chip-blue">
              <span className="nx-live-dot" /> beta pública
            </span>
            <h1 className="font-display text-4xl sm:text-6xl lg:text-7xl font-bold leading-[0.98] mt-6">
              Onde comunidades <br />
              e criadores <span className="nx-text-gradient">se encontram.</span>
            </h1>
            <p className="mt-6 text-base sm:text-lg max-w-xl text-muted-foreground leading-relaxed">
              Nexus Live reúne servidores em tempo real, canais de conversa e transmissões ao vivo numa só plataforma —
              sem seguidores fantasmas, só pessoas conversando, criando e assistindo juntas.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to={goTo}>
                <Button size="lg" data-testid="landing-cta-primary" className="rounded-xl nx-btn-blue border-0 px-7 h-12 font-semibold">
                  Começar agora <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/app/explorar">
                <Button size="lg" variant="outline" data-testid="landing-cta-explore" className="rounded-xl border-[hsl(var(--nx-border))] px-7 h-12 hover:bg-white/5 hover:border-[hsl(var(--nx-cyan))]">
                  <Play className="w-4 h-4 mr-2" /> Explorar lives
                </Button>
              </Link>
            </div>

            <div className="mt-10 flex items-center gap-8">
              {[
                { n: "12k+", l: "criadores ativos" },
                { n: "1.4M", l: "mensagens/dia" },
                { n: "99.9%", l: "uptime" },
              ].map((s) => (
                <div key={s.l}>
                  <div className="font-display font-bold text-2xl">{s.n}</div>
                  <div className="text-[11px] uppercase tracking-widest text-muted-foreground">{s.l}</div>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Preview card */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15, duration: 0.6 }} className="nx-glass rounded-3xl p-4 nx-glow-blue">
            <div className="aspect-video rounded-2xl bg-gradient-to-br from-[hsl(217,60%,20%)] via-[hsl(224,40%,14%)] to-[hsl(300,50%,18%)] relative overflow-hidden">
              <div className="absolute inset-0 nx-scanline" />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-20 h-20 rounded-full bg-white/10 backdrop-blur border border-white/20 grid place-items-center">
                  <Play className="w-8 h-8 text-white ml-1" />
                </div>
              </div>
              <div className="absolute top-3 left-3 nx-chip nx-chip-red">
                <span className="nx-live-dot" style={{background:'#fff'}} /> AO VIVO
              </div>
              <div className="absolute top-3 right-3 nx-chip">12.4K</div>
              <div className="absolute bottom-4 left-4 right-4">
                <div className="text-white font-display font-semibold text-lg leading-tight">Cyber Arena · Grand Final</div>
                <div className="text-white/70 text-xs">@kaelzin · FPS competitivo</div>
              </div>
            </div>
            <div className="mt-4 grid grid-cols-3 gap-2">
              {trending.map((t) => (
                <div key={t.title} className={`aspect-video rounded-lg bg-gradient-to-br ${t.from} ${t.to} p-2 relative overflow-hidden`}>
                  <div className="absolute inset-0 nx-scanline" />
                  <div className="relative flex items-start justify-between">
                    <span className="nx-chip nx-chip-red !py-0.5 !px-1.5 !text-[8px]">LIVE</span>
                    <span className="text-white/80 text-[9px] font-semibold">{t.viewers}</span>
                  </div>
                  <div className="absolute bottom-2 left-2 right-2">
                    <div className="text-white text-[10px] font-semibold truncate">{t.title}</div>
                    <div className="text-white/60 text-[9px] truncate">{t.cat}</div>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Features */}
        <div className="mt-24">
          <div className="flex items-end justify-between mb-8">
            <div>
              <div className="nx-chip nx-chip-cyan mb-3">Recursos</div>
              <h2 className="font-display text-3xl sm:text-4xl font-bold">Feito para conversa <span className="nx-text-gradient">viva</span>.</h2>
            </div>
            <Link to="/register" className="hidden sm:inline-flex items-center gap-2 text-sm text-[hsl(var(--nx-cyan))] hover:underline">
              Começar grátis <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {features.map((f, i) => (
              <motion.div
                key={f.title}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.05 * i, duration: 0.4 }}
                className="group relative nx-panel p-6 hover:border-[hsl(var(--nx-blue))] transition-colors"
                data-testid={`landing-feature-${f.title.split(" ")[0].toLowerCase()}`}
              >
                <div className="w-10 h-10 rounded-lg grid place-items-center" style={{ background: `${f.accent}22`, color: f.accent }}>
                  <f.icon className="w-5 h-5" />
                </div>
                <h3 className="mt-5 font-display text-lg font-semibold">{f.title}</h3>
                <p className="mt-1 text-sm text-muted-foreground">{f.text}</p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="mt-24 nx-glass rounded-3xl p-10 sm:p-14 relative overflow-hidden">
          <div className="absolute -top-20 -right-20 w-[380px] h-[380px] rounded-full bg-[hsl(var(--nx-blue)/0.22)] blur-[100px]" />
          <div className="absolute -bottom-24 -left-16 w-[320px] h-[320px] rounded-full bg-[hsl(var(--nx-cyan)/0.16)] blur-[100px]" />
          <div className="relative">
            <div className="nx-chip nx-chip-blue mb-4"><Sparkles className="w-3.5 h-3.5"/> comece hoje</div>
            <h2 className="font-display text-3xl sm:text-5xl font-bold leading-tight max-w-2xl">
              Sua próxima <span className="nx-text-gradient">comunidade</span> começa aqui.
            </h2>
            <p className="mt-3 text-muted-foreground max-w-xl">
              Registre-se em segundos, crie um servidor e traga sua tribo. Sem cartão, sem fricção.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to={goTo}>
                <Button size="lg" className="rounded-xl nx-btn-blue border-0 px-7 h-12 font-semibold" data-testid="landing-cta-secondary">
                  Criar conta grátis <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/login">
                <Button size="lg" variant="ghost" className="rounded-xl h-12 hover:bg-white/5">Entrar</Button>
              </Link>
            </div>
          </div>
        </div>
      </main>

      <footer className="relative z-10 border-t border-[hsl(var(--nx-border))] px-6 lg:px-14 py-6 flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-muted-foreground">
        <div className="flex items-center gap-3">
          <NexusLogo size={22} withText={false} />
          <span>© {new Date().getFullYear()} Nexus Live — feito para comunidades vivas.</span>
        </div>
        <div className="flex items-center gap-4">
          <span>Termos</span><span>Privacidade</span><span className="font-mono">v1.0 · mvp</span>
        </div>
      </footer>
    </div>
  );
}
